import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InfoIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Generic model interface for any type of model with required properties
 */
export interface GenericModel {
  modelName: string;
  modelSlug: string;
  modelDescription?: string;
}

/**
 * Generic props for the AbstractModelSelectionPill component
 */
export interface AbstractModelSelectionPillProps<T extends GenericModel> {
  models?: T[];
  selectedModel?: T;
  onModelSelect?: (model: T) => void;
  labelPrefix: string;
  dropdownTitle: string;
  renderTooltipContent?: (model: T) => React.ReactNode;
  showInfoIcon?: boolean;
  getDisplayText?: (modelName: string) => string;
}

/**
 * AbstractModelSelectionPill Component
 *
 * A base UI component that displays a dropdown pill for selecting models.
 */
export function AbstractModelSelectionPill<T extends GenericModel>({
  models = [],
  selectedModel,
  onModelSelect,
  labelPrefix,
  dropdownTitle,
  renderTooltipContent,
  showInfoIcon = true,
  getDisplayText,
}: AbstractModelSelectionPillProps<T>) {
  const [selected, setSelected] = useState<string>(
    selectedModel?.modelName || "",
  );

  useEffect(() => {
    if (selectedModel && selectedModel.modelName !== selected) {
      setSelected(selectedModel.modelName);
    }
  }, [selectedModel, selected]);

  const handleModelChange = (modelName: string) => {
    const model = models.find((m) => m.modelName === modelName);
    if (model) {
      setSelected(modelName);
      onModelSelect?.(model);
    }
  };

  const currentModel = models.find((m) => m.modelName === selected);

  const shouldShowTooltip = (model: T) => {
    return renderTooltipContent || model.modelDescription;
  };

  const getTooltipContent = (model: T) => {
    if (renderTooltipContent) {
      return renderTooltipContent(model);
    }
    return model.modelDescription ? <p>{model.modelDescription}</p> : null;
  };

  const getModelDisplayText = () => {
    if (models.length === 0) {
      return "No models available";
    }
    if (getDisplayText && selected) {
      return getDisplayText(selected);
    }
    return selected || "Select a model";
  };

  const TriggerContent = (
    <div
      className={`bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-xs font-medium flex items-center transition-colors ${
        models.length === 0
          ? "cursor-not-allowed opacity-60"
          : "hover:bg-secondary/80 cursor-pointer"
      }`}
    >
      <span className="opacity-70 mr-1">{labelPrefix}</span>
      <span>{getModelDisplayText()}</span>
      {showInfoIcon && currentModel && shouldShowTooltip(currentModel) && (
        <InfoIcon className="ml-1 h-3 w-3 opacity-70" />
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={100}>
      <div className="relative">
        <DropdownMenu>
          {currentModel && shouldShowTooltip(currentModel) ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <DropdownMenuTrigger asChild disabled={models.length === 0}>
                    {TriggerContent}
                  </DropdownMenuTrigger>
                </span>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="max-w-xs bg-popover text-popover-foreground border border-border shadow-md"
              >
                {getTooltipContent(currentModel)}
              </TooltipContent>
            </Tooltip>
          ) : (
            <DropdownMenuTrigger asChild disabled={models.length === 0}>
              {TriggerContent}
            </DropdownMenuTrigger>
          )}
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuLabel>{dropdownTitle}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={selected}
              onValueChange={handleModelChange}
            >
              {models.length > 0 ? (
                models.map((model) =>
                  shouldShowTooltip(model) ? (
                    <Tooltip key={model.modelName}>
                      <TooltipTrigger asChild>
                        <span className="block">
                          <DropdownMenuRadioItem value={model.modelName}>
                            <div className="flex items-center">
                              <span>{model.modelName}</span>
                              {showInfoIcon && (
                                <InfoIcon className="ml-1 h-3 w-3 opacity-70" />
                              )}
                            </div>
                          </DropdownMenuRadioItem>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        className="max-w-xs bg-popover text-popover-foreground border border-border shadow-md"
                      >
                        {getTooltipContent(model)}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <DropdownMenuRadioItem
                      key={model.modelName}
                      value={model.modelName}
                    >
                      {model.modelName}
                    </DropdownMenuRadioItem>
                  ),
                )
              ) : (
                <DropdownMenuLabel className="text-sm text-secondary-foreground opacity-60">
                  No models available
                </DropdownMenuLabel>
              )}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
}
